import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import api from '../../../_metronic/helpers/api'
import {showAlert} from '../../redux/reducers/alertSlice'
import {stringify} from 'qs'

function useUtils() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const fetchVideos = async (
    text = null,
    limit = 12,
    offset = 0,
    programId,
    courseId,
    moduleId,
    unitId,
    relations = undefined
  ) => {
    const params = {
      limit,
      offset,
      where: {},
      relations,
      order: {updatedAt: 'DESC'},
    }

    if (text) params.text = text
    if (unitId && unitId !== 'Unit') params.unitId = unitId
    else if (moduleId && moduleId !== 'Module') params.moduleId = moduleId
    else if (courseId && courseId !== 'Course') params.courseId = courseId
    else if (programId && programId !== 'Program') params.programId = programId

    try {
      const response = await api.get(`/video?${stringify(params)}`)
      if (response.data.status) {
        return response.data.data
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message || 'Network error',
          variant: 'danger',
        })
      )
    }
  }

  const refetchVideo = async (videoId) => {
    try {
      const response = await api.post(`/video/${videoId}/retry-vimeo`)
      if (response.data.status) {
        return response.data.data
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message || 'Network error',
          variant: 'danger',
        })
      )
    }
  }

  const getVideo = async (videoId) => {
    try {
      const response = await api.get(`/video/${videoId}`)
      if (response.data.status) {
        return response.data.data
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message || 'Network error',
          variant: 'danger',
        })
      )
    }
  }

  const fetchAllVideos = async (text = null, programId, courseId, moduleId, unitId) => {
    const params = {
      limit: 100,
      offset: 0,
      relations: ['unitVideo.unit', 'language', 'tag'],
      order: {createdAt: 'DESC'},
      where: {},
    }

    if (text) params.text = text
    if (unitId && unitId !== 'Unit') params.unitId = unitId
    else if (moduleId && moduleId !== 'Module') params.moduleId = moduleId
    else if (courseId && courseId !== 'Course') params.courseId = courseId
    else if (programId && programId !== 'Program') params.programId = programId
    let allData = []
    let totalRecords = 0

    try {
      const initialResponse = await api.get(`/video?${stringify(params)}`)
      if (initialResponse.data.status) {
        totalRecords = initialResponse.data.data.count
        allData = initialResponse.data.data.records

        const totalChunks = Math.ceil(totalRecords / params.limit)
        const promises = []

        for (let i = 1; i < totalChunks; i++) {
          params.offset = i * params.limit
          promises.push(api.get(`/video?${stringify(params)}`))
        }

        const responses = await Promise.all(promises)
        responses.forEach((response) => {
          if (response.data.status) {
            allData = [...allData, ...response.data.data.records]
          }
        })

        if (allData.length > 0) {
          return allData.map((record) => ({
            id: record?.id,
            title: record?.title,
            link: record?.link,
            description: record?.description,
            tags: record?.tag?.map((item) => item?.name),
            duration: record?.duration,
            status: record?.status,
            language: record?.languageId,
            unit: record?.unitId,
          }))
        }
      }
    } catch (error) {
      console.log(error)
      dispatch(
        showAlert({
          message: error?.response?.data?.message || 'Network error',
          variant: 'danger',
        })
      )
    }
  }

  const deleteVideo = async (id) => {
    try {
      const response = await api.delete(`/video/${id}`)
      if (response?.data?.status) {
        dispatch(showAlert({message: 'Successfully deleted', variant: 'success'}))
        return fetchVideos(null, 12, 0, null, null, null, null, ['unitVideo.unit', 'language'])
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message,
          variant: 'danger',
        })
      )
    }
  }

  const fetchLanguages = async () => {
    try {
      const response = await api.get(
        `/language?select[id]=true&select[name]=true&select[createdAt]=true&order[name]=ASC`,
        {
          params: {
            returnAllRecords: true,
            offset: 0,
          },
        }
      )
      if (response.data.status) {
        return response.data.data.records
      }
    } catch (error) {}
  }

  const fetchProgram = async (nextPageToken) => {
    try {
      const params = {
        limit: 100,
        sendToken: true,
      }

      if (nextPageToken) {
        params.nextPageToken = nextPageToken
      }

      const response = await api.get(
        `/program?select[id]=true&select[title]=true&select[createdAt]=true&order[title]=ASC&${stringify(
          params
        )}`
      )
      if (response.data.status) {
        return {
          records: response.data.data.records || [],
          nextPageToken: response.data.data.nextPageToken || null,
        }
      }
      return {records: [], nextPageToken: null}
    } catch (error) {
      console.log(error?.message)
      return {records: [], nextPageToken: null}
    }
  }

  const fetchCourse = async (id, nextPageToken) => {
    try {
      const params = {
        limit: 1000,
        sendToken: true,
      }

      if (nextPageToken) {
        params.nextPageToken = nextPageToken
      }

      const response = await api.get(
        `/course?where[programCourse][programId]=${id}&select[id]=true&select[title]=true&select[createdAt]=true&order[title]=ASC&${stringify(
          params
        )}`
      )
      if (response.data.status) {
        return {
          records: response.data.data.records || [],
          nextPageToken: response.data.data.nextPageToken || null,
        }
      }
      return {records: [], nextPageToken: null}
    } catch (error) {
      console.log(error?.message)
      return {records: [], nextPageToken: null}
    }
  }

  const fetchModule = async (id, nextPageToken) => {
    try {
      const params = {
        limit: 100,
        sendToken: true,
      }

      if (nextPageToken) {
        params.nextPageToken = nextPageToken
      }

      const response = await api.get(
        `/module?where[courseModule][courseId]=${id}&select[id]=true&select[title]=true&select[createdAt]=true&order[title]=ASC}&${stringify(
          params
        )}`
      )
      if (response.data.status) {
        return {
          records: response.data.data.records || [],
          nextPageToken: response.data.data.nextPageToken || null,
        }
      }
      return {records: [], nextPageToken: null}
    } catch (error) {
      console.log(error?.message)
      return {records: [], nextPageToken: null}
    }
  }

  const fetchUnit = async (id, nextPageToken) => {
    try {
      const params = {
        limit: 100,
        sendToken: true,
      }

      if (nextPageToken) {
        params.nextPageToken = nextPageToken
      }

      const response = await api.get(
        `/unit?where[moduleUnit][moduleId]=${id}&select[id]=true&select[name]=true&select[createdAt]=true&order[name]=ASC&${stringify(
          params
        )}`
      )
      if (response.data.status) {
        return {
          records: response.data.data.records || [],
          nextPageToken: response.data.data.nextPageToken || null,
        }
      }
      return {records: [], nextPageToken: null}
    } catch (error) {
      console.log(error?.message)
      return {records: [], nextPageToken: null}
    }
  }

  const submitVideo = async (data) => {
    try {
      const response = await api.post(`/video`, data)
      if (response?.data?.status) {
        dispatch(showAlert({message: 'Successfully added', variant: 'success'}))
        navigate('/videolist')
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message,
          variant: 'danger',
        })
      )
    }
  }

  const addCSV = async (data) => {
    try {
      const response = await api.post(`/video/add-bulk`, data)
      if (response?.data?.status) {
        return response?.data?.data
      }
    } catch (error) {
      dispatch(showAlert({message: error?.response?.data?.message, variant: 'danger'}))
    }
  }

  const handleEdit = async (id) => {
    const params = {
      relations: ['unitVideo.unit', 'language', 'tag'],
    }

    try {
      const response = await api.get(`/video/${id}?${stringify(params)}`)
      if (response.data.status) {
        return response.data.data
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message,
          variant: 'danger',
        })
      )
    }
  }

  const modifyVideo = async (id, data) => {
    try {
      const response = await api.patch(`/video/${id}`, data)
      if (response.data.status) {
        dispatch(
          showAlert({
            message: `Video Modified`,
            variant: 'success',
            timeout: '2000',
          })
        )
        navigate('/videolist')
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: error?.response?.data?.message,
          variant: 'danger',
        })
      )
    }
  }

  const submitReport = async (data) => {
    try {
      const response = await api.post(`/setting/sendCSVReport`, data)
      return response
    } catch (error) {}
  }

  return {
    fetchVideos,
    deleteVideo,
    fetchLanguages,
    fetchProgram,
    fetchCourse,
    fetchModule,
    fetchUnit,
    submitVideo,
    handleEdit,
    modifyVideo,
    fetchAllVideos,
    addCSV,
    submitReport,
    refetchVideo,
    getVideo,
  }
}

export default useUtils
